import { NavLink } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';

import { cx } from 'utils';
import { NavIcon } from './icons';

/**
 * NavList — the role-driven navigation rail, shared by the desktop Sidebar and
 * the mobile drawer so both stay perfectly in sync.
 *
 * Behaviour:
 *  - Active route gets a gradient-glow pill that *slides* between items via a
 *    shared Framer Motion `layoutId` (namespaced per instance so the desktop
 *    and mobile rails animate independently).
 *  - Hover lifts the row and nudges it right for a tactile, premium feel.
 *  - `collapsed` shrinks the rail to icons only; labels animate out and a
 *    native tooltip preserves discoverability.
 *  - `soon` items render as quiet, non-interactive placeholders.
 *  - `external` items (an outside destination like the simulation platform)
 *    render as a plain anchor opening in a new tab instead of a router link —
 *    they never claim the "active route" pill since the app never navigates
 *    there internally.
 *
 * @param {Array}   items       Nav entries from navConfig.
 * @param {boolean} [collapsed] Icon-only mode (desktop collapse).
 * @param {string}  layoutId    Namespace for the animated active pill.
 * @param {Function} [onNavigate] Called after a link is chosen (closes the drawer).
 * @param {'solid'|'soft'} [tone='solid']  Active style: a solid brand pill with
 *        white text (house look), or a pale brand wash with brand text (the
 *        student skin, after the Teen mockup).
 */
const PILL_SPRING = { type: 'spring', stiffness: 420, damping: 34, mass: 0.8 };

export default function NavList({ items, collapsed = false, layoutId, onNavigate, tone = 'solid' }) {
  const soft = tone === 'soft';
  return (
    <nav className="space-y-1">
      {items.map((item) =>
        item.soon ? (
          <div
            key={item.label}
            title={collapsed ? `${item.label} — coming soon` : undefined}
            className={cx(
              'flex cursor-not-allowed items-center rounded-xl px-3 py-2.5 text-sm text-slate-400',
              collapsed ? 'justify-center' : 'justify-between gap-3'
            )}
          >
            <span className="flex items-center gap-3">
              <NavIcon name={item.icon} className="shrink-0 opacity-70" aria-hidden />
              {!collapsed && <span>{item.label}</span>}
            </span>
            {!collapsed && (
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-2xs font-medium text-slate-500">
                Soon
              </span>
            )}
          </div>
        ) : item.external ? (
          <a
            key={item.label}
            href={item.to}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onNavigate}
            title={collapsed ? item.label : undefined}
            className="block focus:outline-none"
          >
            <motion.span
              whileHover={{ x: collapsed ? 0 : 3 }}
              whileTap={{ scale: 0.98 }}
              transition={{ type: 'spring', stiffness: 500, damping: 30 }}
              className={cx(
                'group relative flex items-center rounded-xl px-3 py-2.5 text-sm font-medium text-slate-600',
                'transition-colors duration-200 ease-premium hover:bg-slate-100 hover:text-slate-900',
                collapsed ? 'justify-center' : 'gap-3'
              )}
            >
              <NavIcon
                name={item.icon}
                className="shrink-0 text-slate-400 transition-colors group-hover:text-slate-700"
                aria-hidden
              />
              <AnimatePresence initial={false}>
                {!collapsed && (
                  <motion.span
                    initial={{ opacity: 0, width: 0 }}
                    animate={{ opacity: 1, width: 'auto' }}
                    exit={{ opacity: 0, width: 0 }}
                    transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
                    className="overflow-hidden whitespace-nowrap"
                  >
                    {item.label}
                  </motion.span>
                )}
              </AnimatePresence>
            </motion.span>
          </a>
        ) : (
          <NavLink
            key={item.label}
            to={item.to}
            end={!item.nested}
            onClick={onNavigate}
            title={collapsed ? item.label : undefined}
            className="block focus:outline-none"
          >
            {({ isActive }) => {
              return (
              <motion.span
                whileHover={{ x: collapsed ? 0 : 3 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                className={cx(
                  // `isolate` keeps the -z-10 active pill inside this item; without
                  // it the pill sinks behind the rail's own background and the
                  // active item reads as a blank (white icon on white).
                  'group relative isolate flex items-center rounded-xl px-3 py-2.5 text-sm font-medium',
                  'transition-colors duration-200 ease-premium',
                  collapsed ? 'justify-center' : 'gap-3',
                  isActive
                    ? soft
                      ? 'font-semibold text-brand-700'
                      : 'text-white font-semibold'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                )}
              >
                {isActive && (
                  <motion.span
                    layoutId={layoutId}
                    transition={PILL_SPRING}
                    className={cx('absolute inset-0 -z-10 rounded-xl', soft ? 'bg-brand-50' : 'bg-brand-600')}
                  />
                )}
                <NavIcon
                  name={item.icon}
                  className={cx(
                    'shrink-0 transition-colors',
                    isActive
                      ? soft
                        ? 'text-brand-700'
                        : 'text-white'
                      : soft
                        ? 'text-slate-500 group-hover:text-slate-700'
                        : 'text-slate-400 group-hover:text-slate-600'
                  )}
                  aria-hidden
                />
                <AnimatePresence initial={false}>
                  {!collapsed && (
                    <motion.span
                      initial={{ opacity: 0, width: 0 }}
                      animate={{ opacity: 1, width: 'auto' }}
                      exit={{ opacity: 0, width: 0 }}
                      transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
                      className="overflow-hidden whitespace-nowrap"
                    >
                      {item.label}
                    </motion.span>
                  )}
                </AnimatePresence>
              </motion.span>
              );
            }}
          </NavLink>
        )
      )}
    </nav>
  );
}
